import React, { useState, useEffect } from 'react';
import { Container, Row, Col, Card, Table, Button, Badge, Tab, Tabs, Modal, Form, Alert, Spinner, InputGroup } from 'react-bootstrap';
import { toast } from 'react-toastify';
import axios from 'axios';
import { useLocation } from 'react-router-dom';
import OrgPrintChrome from './OrgPrintChrome';
import { useAuth } from '../contexts/AuthContext';

function ServicesMaster() {
  // Tech Support reviews, tidies and merges services; creating and deleting them is System Admin's
  const { user } = useAuth();
  const canCurate = !!user?.isSystemAdmin;
  const location = useLocation();
  const [loading, setLoading] = useState(true);
  
  // Initialize activeTab from localStorage or default
  // Use component-specific key since this is nested within SystemAdmin
  const storageKey = `activeTab_${location.pathname}_ServicesMaster`;
  const [activeTab, setActiveTab] = useState(() => {
    const saved = localStorage.getItem(storageKey);
    return saved || 'services';
  });
  
  // Save activeTab to localStorage whenever it changes
  useEffect(() => {
    localStorage.setItem(storageKey, activeTab);
  }, [activeTab, storageKey]);
  const [serviceTypes, setServiceTypes] = useState([]);
  const [services, setServices] = useState([]);
  const [filteredServices, setFilteredServices] = useState([]);
  const [searchTerm, setSearchTerm] = useState('');
  const [selectedServiceTypeFilter, setSelectedServiceTypeFilter] = useState('ALL');
  const [verifiedFilter, setVerifiedFilter] = useState('ALL'); // 'ALL' | 'PENDING' | 'VERIFIED'

  const [savingVerify, setSavingVerify] = useState(false);

  const [showMergeModal, setShowMergeModal] = useState(false);
  const [mergeTargetService, setMergeTargetService] = useState(null);
  const [mergeCanonicalId, setMergeCanonicalId] = useState('');
  const [savingMerge, setSavingMerge] = useState(false);

  // Service Type Modal
  const [showServiceTypeModal, setShowServiceTypeModal] = useState(false);
  const [selectedServiceType, setSelectedServiceType] = useState(null);
  const [serviceTypeFormData, setServiceTypeFormData] = useState({
    name: '',
    description: '',
    isActive: true
  });

  // Print view state
  const [showPrintModal, setShowPrintModal] = useState(false);
  const [printData, setPrintData] = useState(null);
  const [printType, setPrintType] = useState(null);

  // Service Modal
  const [showServiceModal, setShowServiceModal] = useState(false);
  const [selectedService, setSelectedService] = useState(null);
  const [serviceFormData, setServiceFormData] = useState({
    name: '',
    serviceCode: '',
    description: '',
    serviceTypeId: '',
    hourlyRate: '',
    dailyRate: '',
    fixedPrice: '',
    pricingModel: '',
    estimatedDurationHours: '',
    unitOfMeasurement: '',
    isActive: true
  });

  useEffect(() => {
    if (activeTab === 'service-types') {
      fetchServiceTypes();
    } else {
      fetchServices();
    }
  }, [activeTab]);

  useEffect(() => {
    filterServices();
  }, [services, searchTerm, selectedServiceTypeFilter, verifiedFilter]);

  const fetchServiceTypes = async () => {
    try {
      setLoading(true);
      const response = await axios.get('/api/system-admin/services-master/service-types');
      setServiceTypes(response.data);
    } catch (error) {
      toast.error('Error fetching service types');
      console.error(error);
    } finally {
      setLoading(false);
    }
  };

  const fetchServices = async () => {
    try {
      setLoading(true);
      const response = await axios.get('/api/system-admin/services-master/services');
      setServices(response.data);
      setFilteredServices(response.data);
    } catch (error) {
      toast.error('Error fetching services');
      console.error(error);
    } finally {
      setLoading(false);
    }
  };

  const filterServices = () => {
    let filtered = services;

    if (verifiedFilter === 'PENDING') {
      filtered = filtered.filter(service => service.verified === false);
    } else if (verifiedFilter === 'VERIFIED') {
      filtered = filtered.filter(service => service.verified !== false);
    }

    // Filter by service type
    if (selectedServiceTypeFilter !== 'ALL') {
      filtered = filtered.filter(service => 
        service.serviceType && service.serviceType.id.toString() === selectedServiceTypeFilter
      );
    }

    // Filter by search term
    if (searchTerm.trim()) {
      const term = searchTerm.toLowerCase();
      filtered = filtered.filter(service =>
        service.name.toLowerCase().includes(term) ||
        (service.serviceCode && service.serviceCode.toLowerCase().includes(term)) ||
        (service.description && service.description.toLowerCase().includes(term))
      );
    }

    setFilteredServices(filtered);
  };

  const handleCreateServiceType = () => {
    setServiceTypeFormData({
      name: '',
      description: '',
      isActive: true
    });
    setSelectedServiceType(null);
    setShowServiceTypeModal(true);
  };

  const handleEditServiceType = (serviceType) => {
    setServiceTypeFormData({
      name: serviceType.name,
      description: serviceType.description || '',
      isActive: serviceType.isActive
    });
    setSelectedServiceType(serviceType);
    setShowServiceTypeModal(true);
  };

  const handleViewPrint = (data, type) => {
    setPrintData(data);
    setPrintType(type);
    setShowPrintModal(true);
  };

  const handlePrint = () => {
    window.print();
  };

  const handleDeleteServiceType = async (id, isSystem) => {
    if (isSystem) {
      toast.error('Cannot delete system service type');
      return;
    }

    if (!window.confirm('Are you sure you want to delete this service type?')) {
      return;
    }

    try {
      await axios.delete(`/api/system-admin/services-master/service-types/${id}`);
      toast.success('Service type deleted successfully');
      fetchServiceTypes();
    } catch (error) {
      const errorMsg = error.response?.data?.error || 'Error deleting service type';
      toast.error(errorMsg);
    }
  };

  const handleSaveServiceType = async (e) => {
    e.preventDefault();
    try {
      if (selectedServiceType) {
        await axios.put(`/api/system-admin/services-master/service-types/${selectedServiceType.id}`, serviceTypeFormData);
        toast.success('Service type updated successfully');
      } else {
        await axios.post('/api/system-admin/services-master/service-types', serviceTypeFormData);
        toast.success('Service type created successfully');
      }
      setShowServiceTypeModal(false);
      fetchServiceTypes();
    } catch (error) {
      const errorMsg = error.response?.data?.error || 'Error saving service type';
      toast.error(errorMsg);
    }
  };

  const handleCreateService = () => {
    setServiceFormData({
      name: '',
      serviceCode: '',
      description: '',
      serviceTypeId: serviceTypes.length > 0 ? serviceTypes.filter(st => st.isActive)[0]?.id.toString() : '',
      hourlyRate: '',
      dailyRate: '',
      fixedPrice: '',
      pricingModel: '',
      estimatedDurationHours: '',
      unitOfMeasurement: '',
      isActive: true
    });
    setSelectedService(null);
    setShowServiceModal(true);
  };

  const handleEditService = (service) => {
    setServiceFormData({
      name: service.name,
      serviceCode: service.serviceCode || '',
      description: service.description || '',
      serviceTypeId: service.serviceType ? service.serviceType.id.toString() : '',
      hourlyRate: service.hourlyRate || '',
      dailyRate: service.dailyRate || '',
      fixedPrice: service.fixedPrice || '',
      pricingModel: service.pricingModel || '',
      estimatedDurationHours: service.estimatedDurationHours || '',
      unitOfMeasurement: service.unitOfMeasurement || '',
      isActive: service.isActive
    });
    setSelectedService(service);
    setShowServiceModal(true);
  };

  const handleDeleteService = async (id, isSystem) => {
    if (isSystem) {
      toast.error('Cannot delete system service');
      return;
    }

    if (!window.confirm('Are you sure you want to delete this service?')) {
      return;
    }

    try {
      await axios.delete(`/api/system-admin/services-master/services/${id}`);
      toast.success('Service deleted successfully');
      fetchServices();
    } catch (error) {
      const errorMsg = error.response?.data?.error || 'Error deleting service';
      toast.error(errorMsg);
    }
  };

  const handleVerifyService = async (service) => {
    setSavingVerify(true);
    try {
      await axios.post(`/api/system-admin/services-master/services/${service.id}/verify`);
      toast.success('Service verified');
      fetchServices();
    } catch (error) {
      toast.error(error.response?.data?.error || 'Error verifying service');
    } finally {
      setSavingVerify(false);
    }
  };

  const handleOpenMerge = (service) => {
    setMergeTargetService(service);
    setMergeCanonicalId('');
    setShowMergeModal(true);
  };

  const handleSaveMerge = async (e) => {
    e.preventDefault();
    if (!mergeCanonicalId) return;
    setSavingMerge(true);
    try {
      await axios.post(`/api/system-admin/services-master/services/${mergeTargetService.id}/merge-into/${mergeCanonicalId}`);
      toast.success('Service merged into the canonical record');
      setShowMergeModal(false);
      fetchServices();
    } catch (error) {
      toast.error(error.response?.data?.error || 'Error merging service');
    } finally {
      setSavingMerge(false);
    }
  };

  const handleSaveService = async (e) => {
    e.preventDefault();
    try {
      const payload = {
        ...serviceFormData,
        serviceType: { id: parseInt(serviceFormData.serviceTypeId) },
        hourlyRate: serviceFormData.hourlyRate ? parseFloat(serviceFormData.hourlyRate) : null,
        dailyRate: serviceFormData.dailyRate ? parseFloat(serviceFormData.dailyRate) : null,
        fixedPrice: serviceFormData.fixedPrice ? parseFloat(serviceFormData.fixedPrice) : null,
        estimatedDurationHours: serviceFormData.estimatedDurationHours ? parseFloat(serviceFormData.estimatedDurationHours) : null
      };
      delete payload.serviceTypeId;

      if (selectedService) {
        await axios.put(`/api/system-admin/services-master/services/${selectedService.id}`, payload);
        toast.success('Service updated successfully');
      } else {
        await axios.post('/api/system-admin/services-master/services', payload);
        toast.success('Service created successfully');
      }
      setShowServiceModal(false);
      fetchServices();
    } catch (error) {
      const errorMsg = error.response?.data?.error || 'Error saving service';
      toast.error(errorMsg);
    }
  };

  // Load service types when switching to services tab
  useEffect(() => {
    if (activeTab === 'services' && serviceTypes.length === 0) {
      fetchServiceTypes();
    }
  }, [activeTab]);

  if (loading && (serviceTypes.length === 0 && services.length === 0)) {
    return (
      <Container className="mt-4">
        <div className="d-flex justify-content-center">
          <Spinner animation="border" role="status">
            <span className="visually-hidden">Loading...</span>
          </Spinner>
        </div>
      </Container>
    );
  }

  return (
    <Container fluid className="mt-4">
      <Row className="mb-4">
        <Col>
          <p className="text-muted mb-0">Manage system-wide services and service types master data</p>
        </Col>
      </Row>

      <Tabs activeKey={activeTab} onSelect={(k) => setActiveTab(k)} className="mb-4">
        {/* Services Tab */}
        <Tab eventKey="services" title="Services">
          <Card>
            <Card.Header>
              <div className="d-flex justify-content-between align-items-center flex-wrap gap-2">
                <h5 className="mb-0">Services Master</h5>
                {canCurate && (
                  <Button variant="primary" onClick={handleCreateService}>
                    + Create Service
                  </Button>
                )}
              </div>
            </Card.Header>
            <Card.Body>
              <Row className="mb-3">
                <Col md={6}>
                  <InputGroup>
                    <InputGroup.Text>🔍</InputGroup.Text>
                    <Form.Control
                      type="text"
                      placeholder="Search by name, service code, or description..."
                      value={searchTerm}
                      onChange={(e) => setSearchTerm(e.target.value)}
                    />
                  </InputGroup>
                </Col>
                <Col md={4}>
                  <Form.Select
                    value={selectedServiceTypeFilter}
                    onChange={(e) => setSelectedServiceTypeFilter(e.target.value)}
                  >
                    <option value="ALL">All Service Types</option>
                    {serviceTypes.filter(st => st.isActive).map(serviceType => (
                      <option key={serviceType.id} value={serviceType.id.toString()}>
                        {serviceType.name}
                      </option>
                    ))}
                  </Form.Select>
                </Col>
                <Col md={2}>
                  <div className="text-muted small">
                    {filteredServices.length} services
                  </div>
                </Col>
              </Row>

              <div className="mb-3 d-flex gap-2">
                {['ALL', 'PENDING', 'VERIFIED'].map((f) => (
                  <Button
                    key={f}
                    size="sm"
                    variant={verifiedFilter === f ? 'primary' : 'outline-secondary'}
                    onClick={() => setVerifiedFilter(f)}
                  >
                    {f === 'ALL' ? 'All' : f === 'PENDING' ? 'Pending Review' : 'Verified'}
                    {f === 'PENDING' && ` (${services.filter((s) => s.verified === false).length})`}
                  </Button>
                ))}
              </div>

              {loading ? (
                <div className="d-flex justify-content-center">
                  <Spinner animation="border" role="status" />
                </div>
              ) : (
                <Table striped bordered hover responsive>
                  <thead>
                    <tr>
                      <th>Name</th>
                      <th>Service Code</th>
                      <th>Service Type</th>
                      <th>Pricing Model</th>
                      <th>Rate/Price</th>
                      <th>UOM</th>
                      <th>Status</th>
                      <th>Actions</th>
                    </tr>
                  </thead>
                  <tbody>
                    {filteredServices.map((service) => (
                      <tr key={service.id}>
                        <td>{service.name}</td>
                        <td>{service.serviceCode || '-'}</td>
                        <td>{service.serviceType?.name || '-'}</td>
                        <td>{service.pricingModel || '-'}</td>
                        <td>
                          {service.pricingModel === 'HOURLY' && service.hourlyRate && 
                            `$${parseFloat(service.hourlyRate).toFixed(2)}/hr`}
                          {service.pricingModel === 'DAILY' && service.dailyRate && 
                            `$${parseFloat(service.dailyRate).toFixed(2)}/day`}
                          {service.pricingModel === 'FIXED' && service.fixedPrice && 
                            `$${parseFloat(service.fixedPrice).toFixed(2)}`}
                          {service.pricingModel === 'PROJECT_BASED' && service.fixedPrice && 
                            `$${parseFloat(service.fixedPrice).toFixed(2)}`}
                          {!service.pricingModel && '-'}
                        </td>
                        <td>{service.unitOfMeasurement || '-'}</td>
                        <td>
                          <Badge bg={service.isActive ? 'success' : 'secondary'}>
                            {service.isActive ? 'Active' : 'Inactive'}
                          </Badge>
                          {service.isSystem && (
                            <Badge bg="info" className="ms-1">System</Badge>
                          )}
                          {service.verified === false && (
                            <div className="mt-1">
                              <Badge bg="warning" text="dark">Pending Review</Badge>
                              {service.createdByOrganization?.name && (
                                <div className="small text-muted mt-1">
                                  Requested by {service.createdByOrganization.name}
                                </div>
                              )}
                            </div>
                          )}
                        </td>
                        <td>
                          <Button
                            size="sm"
                            variant="outline-info"
                            className="me-2"
                            onClick={() => handleViewPrint(service, 'SERVICE')}
                            title="View & Print"
                          >
                            <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" fill="currentColor" viewBox="0 0 16 16" style={{ verticalAlign: 'middle' }}>
                              <path d="M5 1a2 2 0 0 0-2 2v1h10V3a2 2 0 0 0-2-2H5zm6 8H5a1 1 0 0 0-1 1v3a1 1 0 0 0 1 1h6a1 1 0 0 0 1-1v-3a1 1 0 0 0-1-1z"/>
                              <path d="M0 7a2 2 0 0 1 2-2h12a2 2 0 0 1 2 2v3a2 2 0 0 1-2 2h-1v-2a2 2 0 0 0-2-2H5a2 2 0 0 0-2 2v2H2a2 2 0 0 1-2-2V7zm2.5 1a.5.5 0 1 0 0-1 .5.5 0 0 0 0 1z"/>
                            </svg>
                          </Button>
                          <Button
                            size="sm"
                            variant="outline-primary"
                            className="me-2"
                            onClick={() => handleEditService(service)}
                            title="Edit"
                          >
                            <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" fill="currentColor" viewBox="0 0 16 16" style={{ verticalAlign: 'middle' }}>
                              <path d="M15.502 1.94a.5.5 0 0 1 0 .706L14.459 3.69l-2-2L13.502.646a.5.5 0 0 1 .707 0l1.293 1.293zm-1.75 2.456-2-2L4.939 9.21a.5.5 0 0 0-.121.196l-.805 2.414a.25.25 0 0 0 .316.316l2.414-.805a.5.5 0 0 0 .196-.12l6.813-6.814z"/>
                              <path fillRule="evenodd" d="M1 13.5A1.5 1.5 0 0 0 2.5 15h11a1.5 1.5 0 0 0 1.5-1.5v-6a.5.5 0 0 0-1 0v6a.5.5 0 0 1-.5.5h-11a.5.5 0 0 1-.5-.5v-11a.5.5 0 0 1 .5-.5H9a.5.5 0 0 0 0-1H2.5A1.5 1.5 0 0 0 1 2.5v11z"/>
                            </svg>
                          </Button>
                          {service.verified === false && (
                            <>
                              <Button
                                size="sm"
                                variant="outline-success"
                                className="me-2"
                                onClick={() => handleVerifyService(service)}
                                disabled={savingVerify}
                              >
                                Verify
                              </Button>
                              <Button
                                size="sm"
                                variant="outline-warning"
                                className="me-2"
                                onClick={() => handleOpenMerge(service)}
                              >
                                Merge into…
                              </Button>
                            </>
                          )}
                          {canCurate && (
<Button
                            size="sm"
                            variant="outline-danger"
                            onClick={() => handleDeleteService(service.id, service.isSystem)}
                            disabled={service.isSystem}
                            title="Delete"
                          >
                            <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" fill="currentColor" viewBox="0 0 16 16" style={{ verticalAlign: 'middle' }}>
                              <path d="M5.5 5.5A.5.5 0 0 1 6 6v6a.5.5 0 0 1-1 0V6a.5.5 0 0 1 .5-.5zm2.5 0a.5.5 0 0 1 .5.5v6a.5.5 0 0 1-1 0V6a.5.5 0 0 1 .5-.5zm3 .5a.5.5 0 0 0-1 0v6a.5.5 0 0 0 1 0V6z"/>
                              <path fillRule="evenodd" d="M14.5 3a1 1 0 0 1-1 1H13v9a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2V4h-.5a1 1 0 0 1-1-1V2a1 1 0 0 1 1-1H6a1 1 0 0 1 1-1h2a1 1 0 0 1 1 1h3.5a1 1 0 0 1 1 1v1zM4.118 4 4 4.059V13a1 1 0 0 0 1 1h6a1 1 0 0 0 1-1V4.059L11.882 4H4.118zM2.5 3V2h11v1h-11z"/>
                            </svg>
                          </Button>
)}
                        </td>
                      </tr>
                    ))}
                    {filteredServices.length === 0 && (
                      <tr>
                        <td colSpan="8" className="text-center text-muted">
                          No services found. {searchTerm || selectedServiceTypeFilter !== 'ALL' 
                            ? 'Try adjusting your filters.' 
                            : 'Create a service to get started.'}
                        </td>
                      </tr>
                    )}
                  </tbody>
                </Table>
              )}
            </Card.Body>
          </Card>
        </Tab>

        {/* Service Types Tab */}
        <Tab eventKey="service-types" title="Service Types">
          <Card>
            <Card.Header>
              <div className="d-flex justify-content-between align-items-center flex-wrap gap-2">
                <h5 className="mb-0">Service Types Master</h5>
                {canCurate && (
                  <Button variant="primary" onClick={handleCreateServiceType}>
                    + Create Service Type
                  </Button>
                )}
              </div>
            </Card.Header>
            <Card.Body>
              {loading ? (
                <div className="d-flex justify-content-center">
                  <Spinner animation="border" role="status" />
                </div>
              ) : (
                <Table striped bordered hover responsive>
                  <thead>
                    <tr>
                      <th>Name</th>
                      <th>Description</th>
                      <th>Status</th>
                      <th>Actions</th>
                    </tr>
                  </thead>
                  <tbody>
                    {serviceTypes.map((serviceType) => (
                      <tr key={serviceType.id}>
                        <td>
                          {serviceType.name}
                          {serviceType.isSystem && (
                            <Badge bg="info" className="ms-2">System</Badge>
                          )}
                        </td>
                        <td>{serviceType.description || '-'}</td>
                        <td>
                          <Badge bg={serviceType.isActive ? 'success' : 'secondary'}>
                            {serviceType.isActive ? 'Active' : 'Inactive'}
                          </Badge>
                        </td>
                        <td>
                          <Button
                            size="sm"
                            variant="outline-info"
                            className="me-2"
                            onClick={() => handleViewPrint(serviceType, 'SERVICE_TYPE')}
                            title="View & Print"
                          >
                            <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" fill="currentColor" viewBox="0 0 16 16" style={{ verticalAlign: 'middle' }}>
                              <path d="M5 1a2 2 0 0 0-2 2v1h10V3a2 2 0 0 0-2-2H5zm6 8H5a1 1 0 0 0-1 1v3a1 1 0 0 0 1 1h6a1 1 0 0 0 1-1v-3a1 1 0 0 0-1-1z"/>
                              <path d="M0 7a2 2 0 0 1 2-2h12a2 2 0 0 1 2 2v3a2 2 0 0 1-2 2h-1v-2a2 2 0 0 0-2-2H5a2 2 0 0 0-2 2v2H2a2 2 0 0 1-2-2V7zm2.5 1a.5.5 0 1 0 0-1 .5.5 0 0 0 0 1z"/>
                            </svg>
                          </Button>
                          <Button
                            size="sm"
                            variant="outline-primary"
                            className="me-2"
                            onClick={() => handleEditServiceType(serviceType)}
                            title="Edit"
                          >
                            <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" fill="currentColor" viewBox="0 0 16 16" style={{ verticalAlign: 'middle' }}>
                              <path d="M15.502 1.94a.5.5 0 0 1 0 .706L14.459 3.69l-2-2L13.502.646a.5.5 0 0 1 .707 0l1.293 1.293zm-1.75 2.456-2-2L4.939 9.21a.5.5 0 0 0-.121.196l-.805 2.414a.25.25 0 0 0 .316.316l2.414-.805a.5.5 0 0 0 .196-.12l6.813-6.814z"/>
                              <path fillRule="evenodd" d="M1 13.5A1.5 1.5 0 0 0 2.5 15h11a1.5 1.5 0 0 0 1.5-1.5v-6a.5.5 0 0 0-1 0v6a.5.5 0 0 1-.5.5h-11a.5.5 0 0 1-.5-.5v-11a.5.5 0 0 1 .5-.5H9a.5.5 0 0 0 0-1H2.5A1.5 1.5 0 0 0 1 2.5v11z"/>
                            </svg>
                          </Button>
                          {canCurate && (
<Button
                            size="sm"
                            variant="outline-danger"
                            onClick={() => handleDeleteServiceType(serviceType.id, serviceType.isSystem)}
                            disabled={serviceType.isSystem}
                            title="Delete"
                          >
                            <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" fill="currentColor" viewBox="0 0 16 16" style={{ verticalAlign: 'middle' }}>
                              <path d="M5.5 5.5A.5.5 0 0 1 6 6v6a.5.5 0 0 1-1 0V6a.5.5 0 0 1 .5-.5zm2.5 0a.5.5 0 0 1 .5.5v6a.5.5 0 0 1-1 0V6a.5.5 0 0 1 .5-.5zm3 .5a.5.5 0 0 0-1 0v6a.5.5 0 0 0 1 0V6z"/>
                              <path fillRule="evenodd" d="M14.5 3a1 1 0 0 1-1 1H13v9a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2V4h-.5a1 1 0 0 1-1-1V2a1 1 0 0 1 1-1H6a1 1 0 0 1 1-1h2a1 1 0 0 1 1 1h3.5a1 1 0 0 1 1 1v1zM4.118 4 4 4.059V13a1 1 0 0 0 1 1h6a1 1 0 0 0 1-1V4.059L11.882 4H4.118zM2.5 3V2h11v1h-11z"/>
                            </svg>
                          </Button>
)}
                        </td>
                      </tr>
                    ))}
                    {serviceTypes.length === 0 && (
                      <tr>
                        <td colSpan="4" className="text-center text-muted">
                          No service types found. Create one to get started.
                        </td>
                      </tr>
                    )}
                  </tbody>
                </Table>
              )}
            </Card.Body>
          </Card>
        </Tab>
      </Tabs>

      {/* Service Type Modal */}
      <Modal show={showServiceTypeModal} onHide={() => setShowServiceTypeModal(false)}>
        <Modal.Header closeButton>
          <Modal.Title>{selectedServiceType ? 'Edit Service Type' : 'Create Service Type'}</Modal.Title>
        </Modal.Header>
        <Form onSubmit={handleSaveServiceType}>
          <Modal.Body>
            <Form.Group className="mb-3">
              <Form.Label>Name *</Form.Label>
              <Form.Control
                type="text"
                value={serviceTypeFormData.name}
                onChange={(e) => setServiceTypeFormData({ ...serviceTypeFormData, name: e.target.value })}
                required
              />
            </Form.Group>

            <Form.Group className="mb-3">
              <Form.Label>Description</Form.Label>
              <Form.Control
                as="textarea"
                rows={3}
                value={serviceTypeFormData.description}
                onChange={(e) => setServiceTypeFormData({ ...serviceTypeFormData, description: e.target.value })}
              />
            </Form.Group>

            <Form.Group className="mb-3">
              <Form.Label>Status</Form.Label>
              <Form.Select
                value={serviceTypeFormData.isActive ? 'true' : 'false'}
                onChange={(e) => setServiceTypeFormData({ ...serviceTypeFormData, isActive: e.target.value === 'true' })}
              >
                <option value="true">Active</option>
                <option value="false">Inactive</option>
              </Form.Select>
            </Form.Group>
          </Modal.Body>
          <Modal.Footer>
            <Button variant="secondary" onClick={() => setShowServiceTypeModal(false)}>
              Cancel
            </Button>
            <Button variant="primary" type="submit">
              {selectedServiceType ? 'Update' : 'Create'}
            </Button>
          </Modal.Footer>
        </Form>
      </Modal>

      {/* Print View Modal */}
      <Modal show={showPrintModal} onHide={() => setShowPrintModal(false)} size="xl" className="print-modal">
        <Modal.Header className="print-header">
          <Modal.Title>
            {printType === 'SERVICE_TYPE' && 'Service Type'}
            {printType === 'SERVICE' && 'Service'}
          </Modal.Title>
          <div className="print-actions">
            <Button variant="primary" onClick={handlePrint} className="me-2">Print</Button>
            <Button variant="secondary" onClick={() => setShowPrintModal(false)}>Close</Button>
          </div>
        </Modal.Header>
        <Modal.Body className="print-body">
          <OrgPrintChrome>
          {printType === 'SERVICE_TYPE' && printData && (
            <div className="print-content">
              <div className="text-center mb-4">
                <h2>SERVICE TYPE</h2>
                <p className="text-muted"><strong>{printData.name}</strong></p>
              </div>
              <Row className="mb-3">
                <Col md={6}>
                  <p><strong>Description:</strong> {printData.description || '-'}</p>
                  <p><strong>Active:</strong> {printData.isActive ? 'Yes' : 'No'}</p>
                </Col>
              </Row>
            </div>
          )}
          {printType === 'SERVICE' && printData && (
            <div className="print-content">
              <div className="text-center mb-4">
                <h2>SERVICE</h2>
                <p className="text-muted"><strong>{printData.name}</strong> {printData.serviceCode && `(${printData.serviceCode})`}</p>
              </div>
              <Row className="mb-3">
                <Col md={6}>
                  <p><strong>Service Type:</strong> {printData.serviceType?.name || '-'}</p>
                  <p><strong>Description:</strong> {printData.description || '-'}</p>
                  <p><strong>Pricing Model:</strong> {printData.pricingModel || '-'}</p>
                  <p><strong>Hourly Rate:</strong> {printData.hourlyRate != null ? `$${parseFloat(printData.hourlyRate).toFixed(2)}` : '-'}</p>
                  <p><strong>Daily Rate:</strong> {printData.dailyRate != null ? `$${parseFloat(printData.dailyRate).toFixed(2)}` : '-'}</p>
                  <p><strong>Fixed Price:</strong> {printData.fixedPrice != null ? `$${parseFloat(printData.fixedPrice).toFixed(2)}` : '-'}</p>
                </Col>
                <Col md={6}>
                  <p><strong>Estimated Duration (hrs):</strong> {printData.estimatedDurationHours || '-'}</p>
                  <p><strong>Unit of Measurement:</strong> {printData.unitOfMeasurement || '-'}</p>
                  <p><strong>Active:</strong> {printData.isActive ? 'Yes' : 'No'}</p>
                </Col>
              </Row>
            </div>
          )}
                  </OrgPrintChrome>
        </Modal.Body>
      </Modal>

      {/* Service Modal */}
      <Modal show={showServiceModal} onHide={() => setShowServiceModal(false)} size="lg">
        <Modal.Header closeButton>
          <Modal.Title>{selectedService ? 'Edit Service' : 'Create Service'}</Modal.Title>
        </Modal.Header>
        <Form onSubmit={handleSaveService}>
          <Modal.Body>
            <Row>
              <Col md={6}>
                <Form.Group className="mb-3">
                  <Form.Label>Name *</Form.Label>
                  <Form.Control
                    type="text"
                    value={serviceFormData.name}
                    onChange={(e) => setServiceFormData({ ...serviceFormData, name: e.target.value })}
                    required
                  />
                </Form.Group>
              </Col>
              <Col md={6}>
                <Form.Group className="mb-3">
                  <Form.Label>Service Code</Form.Label>
                  <Form.Control
                    type="text"
                    value={serviceFormData.serviceCode}
                    onChange={(e) => setServiceFormData({ ...serviceFormData, serviceCode: e.target.value })}
                    placeholder="e.g., SV-001"
                  />
                </Form.Group>
              </Col>
            </Row>

            <Row>
              <Col md={6}>
                <Form.Group className="mb-3">
                  <Form.Label>Service Type *</Form.Label>
                  <Form.Select
                    value={serviceFormData.serviceTypeId}
                    onChange={(e) => setServiceFormData({ ...serviceFormData, serviceTypeId: e.target.value })}
                    required
                  >
                    <option value="">Select Service Type</option>
                    {serviceTypes.filter(st => st.isActive).map(serviceType => (
                      <option key={serviceType.id} value={serviceType.id.toString()}>
                        {serviceType.name}
                      </option>
                    ))}
                  </Form.Select>
                </Form.Group>
              </Col>
              <Col md={6}>
                <Form.Group className="mb-3">
                  <Form.Label>Pricing Model</Form.Label>
                  <Form.Select
                    value={serviceFormData.pricingModel}
                    onChange={(e) => setServiceFormData({ ...serviceFormData, pricingModel: e.target.value })}
                  >
                    <option value="">Select Pricing Model</option>
                    <option value="HOURLY">Hourly</option>
                    <option value="DAILY">Daily</option>
                    <option value="FIXED">Fixed Price</option>
                    <option value="PROJECT_BASED">Project Based</option>
                  </Form.Select>
                </Form.Group>
              </Col>
            </Row>

            <Row>
              <Col md={4}>
                <Form.Group className="mb-3">
                  <Form.Label>Hourly Rate</Form.Label>
                  <Form.Control
                    type="number"
                    step="0.01"
                    value={serviceFormData.hourlyRate}
                    onChange={(e) => setServiceFormData({ ...serviceFormData, hourlyRate: e.target.value })}
                    placeholder="0.00"
                  />
                </Form.Group>
              </Col>
              <Col md={4}>
                <Form.Group className="mb-3">
                  <Form.Label>Daily Rate</Form.Label>
                  <Form.Control
                    type="number"
                    step="0.01"
                    value={serviceFormData.dailyRate}
                    onChange={(e) => setServiceFormData({ ...serviceFormData, dailyRate: e.target.value })}
                    placeholder="0.00"
                  />
                </Form.Group>
              </Col>
              <Col md={4}>
                <Form.Group className="mb-3">
                  <Form.Label>Fixed Price</Form.Label>
                  <Form.Control
                    type="number"
                    step="0.01"
                    value={serviceFormData.fixedPrice}
                    onChange={(e) => setServiceFormData({ ...serviceFormData, fixedPrice: e.target.value })}
                    placeholder="0.00"
                  />
                </Form.Group>
              </Col>
            </Row>

            <Row>
              <Col md={6}>
                <Form.Group className="mb-3">
                  <Form.Label>Estimated Duration (Hours)</Form.Label>
                  <Form.Control
                    type="number"
                    step="0.01"
                    value={serviceFormData.estimatedDurationHours}
                    onChange={(e) => setServiceFormData({ ...serviceFormData, estimatedDurationHours: e.target.value })}
                    placeholder="0.00"
                  />
                </Form.Group>
              </Col>
              <Col md={6}>
                <Form.Group className="mb-3">
                  <Form.Label>Unit of Measurement</Form.Label>
                  <Form.Control
                    type="text"
                    value={serviceFormData.unitOfMeasurement}
                    onChange={(e) => setServiceFormData({ ...serviceFormData, unitOfMeasurement: e.target.value })}
                    placeholder="e.g., Hour, Day, Project"
                  />
                </Form.Group>
              </Col>
            </Row>

            <Form.Group className="mb-3">
              <Form.Label>Description</Form.Label>
              <Form.Control
                as="textarea"
                rows={3}
                value={serviceFormData.description}
                onChange={(e) => setServiceFormData({ ...serviceFormData, description: e.target.value })}
                placeholder="Description of the service"
              />
            </Form.Group>

            <Form.Group className="mb-3">
              <Form.Label>Status</Form.Label>
              <Form.Select
                value={serviceFormData.isActive ? 'true' : 'false'}
                onChange={(e) => setServiceFormData({ ...serviceFormData, isActive: e.target.value === 'true' })}
              >
                <option value="true">Active</option>
                <option value="false">Inactive</option>
              </Form.Select>
            </Form.Group>
          </Modal.Body>
          <Modal.Footer>
            <Button variant="secondary" onClick={() => setShowServiceModal(false)}>
              Cancel
            </Button>
            <Button variant="primary" type="submit">
              {selectedService ? 'Update' : 'Create'}
            </Button>
          </Modal.Footer>
        </Form>
      </Modal>

      {/* Merge Service Modal */}
      <Modal show={showMergeModal} onHide={() => setShowMergeModal(false)}>
        <Modal.Header closeButton>
          <Modal.Title>Merge "{mergeTargetService?.name}" into…</Modal.Title>
        </Modal.Header>
        <Form onSubmit={handleSaveMerge}>
          <Modal.Body>
            <p className="text-muted small">
              Every reference to "{mergeTargetService?.name}" (org services, GRNs, purchase orders)
              will be reassigned to the service you pick below, and this draft will be deleted. This
              cannot be undone.
            </p>
            <Form.Group className="mb-3">
              <Form.Label>Canonical service *</Form.Label>
              <Form.Select
                value={mergeCanonicalId}
                onChange={(e) => setMergeCanonicalId(e.target.value)}
                required
              >
                <option value="">Select the service to keep</option>
                {services
                  .filter((s) => s.verified !== false && s.id !== mergeTargetService?.id)
                  .map((s) => (
                    <option key={s.id} value={s.id}>
                      {s.name} {s.serviceType ? `(${s.serviceType.name})` : ''}
                    </option>
                  ))}
              </Form.Select>
            </Form.Group>
          </Modal.Body>
          <Modal.Footer>
            <Button variant="secondary" onClick={() => setShowMergeModal(false)}>Cancel</Button>
            <Button variant="danger" type="submit" disabled={savingMerge || !mergeCanonicalId}>
              {savingMerge ? 'Merging...' : 'Merge'}
            </Button>
          </Modal.Footer>
        </Form>
      </Modal>
    </Container>
  );
}

export default ServicesMaster;

